import React from 'react';
import { fmtCurrency, sentimentClass } from './clientReportingFormat';
import { collapseStrategies } from '../../utils/optionStrategies';

function formatAsOf(iso) {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatQty(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  const num = Number(value);
  if (Number.isInteger(num)) return String(num);
  return num.toLocaleString('en-US', { maximumFractionDigits: 4 });
}

function formatPrice(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  return fmtCurrency(value);
}

function formatRoi(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  const num = Number(value);
  return `${num >= 0 ? '+' : ''}${num.toFixed(2)}%`;
}

function reconciliationNote(broker, supportsPerSymbolPositions) {
  if (broker === 'NINJA') {
    return 'Closed-trade profit does not equal net liquidity. Open P/L comes from Tradovate at the last sync.';
  }
  if (broker === 'COINBASE') {
    return 'Closed-trade profit does not equal account balance. Net P&L uses Coinbase portfolio value; held crypto explains the gap.';
  }
  if (broker === 'IBKR') {
    return supportsPerSymbolPositions
      ? 'Closed-trade profit does not equal account balance. Net P&L uses IBKR NAV; open positions explain the gap.'
      : 'Closed-trade profit does not equal account balance. Add Open Positions to your IBKR Flex query to show held symbols here.';
  }
  return 'Closed-trade profit does not equal account balance. Net P&L uses broker account value; open positions explain the gap.';
}

export function positionsSyncNote(broker, supportsPerSymbolPositions) {
  if (broker === 'NINJA') {
    return 'Open P/L refreshes when the NinjaTrader account is synced.';
  }
  if (broker === 'COINBASE') {
    return 'Open positions refresh when the Coinbase account is synced.';
  }
  if (broker === 'IBKR') {
    return supportsPerSymbolPositions
      ? 'Open positions refresh when the IBKR account is synced.'
      : 'Per-symbol open positions appear after your Flex query includes the Open Positions section.';
  }
  return 'Open positions refresh when the account is synced.';
}

export function OpenPositionsCompact({ positions: rawPositions = [], asOf = null, className = '' }) {
  // Option legs held together (a spread, a covered call) show as one row.
  const positions = collapseStrategies(rawPositions);
  if (!positions.length) return null;

  const totalUnrealized = positions.reduce(
    (sum, row) => sum + (Number(row.unrealizedPl) || 0),
    0,
  );
  const asOfLabel = formatAsOf(asOf);

  return (
    <div className={`vd-open-positions vd-open-positions-compact${className ? ` ${className}` : ''}`}>
      <div className="vd-open-positions-heading">
        <h4>Open positions</h4>
        {asOfLabel ? <span className="vd-open-positions-asof">As of {asOfLabel}</span> : null}
      </div>
      <table className="vd-ml-recent-trades-table vd-open-positions-table">
        <thead>
          <tr>
            <th scope="col">Symbol</th>
            <th scope="col" className="vd-num">Qty</th>
            <th scope="col" className="vd-num">Unrealized P&amp;L</th>
          </tr>
        </thead>
        <tbody>
          {positions.map((row, i) => (
            <tr key={`${row.symbol}-${i}`}>
              <td>
                {row.symbol}
                {row.isStrategy ? <small className="vd-strategy-legs">{row.sideLabel} · {row.legsText}</small> : null}
              </td>
              <td className="vd-num">{row.isStrategy ? `${row.legs.length} legs` : formatQty(row.quantity)}</td>
              <td className={`vd-num ${sentimentClass(row.unrealizedPl)}`}>
                {fmtCurrency(row.unrealizedPl, { signed: true })}
              </td>
            </tr>
          ))}
          <tr className="vd-open-positions-total-row">
            <td colSpan={2}><strong>Total open</strong></td>
            <td className={`vd-num ${sentimentClass(totalUnrealized)}`}>
              <strong>{fmtCurrency(totalUnrealized, { signed: true })}</strong>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export function OpenPositionsDetailRows({ positions: rawPositions = [], asOf = null }) {
  const positions = collapseStrategies(rawPositions);
  if (!positions.length) return null;

  const asOfLabel = formatAsOf(asOf);

  return (
    <>
      <tr className="vd-open-positions-section-row">
        <td colSpan={10}>
          Open positions{asOfLabel ? ` · as of ${asOfLabel}` : ''}
        </td>
      </tr>
      {positions.map((row, i) => (
        <tr key={`open-${row.symbol}-${i}`} className="vd-open-position-row">
          <td>—</td>
          <td>
            <strong>{row.symbol}</strong>
            {row.legsText ? <small className="vd-strategy-legs">{row.legsText}</small> : null}
          </td>
          <td>{row.sideLabel || row.side || 'Long'}</td>
          <td className="vd-num">{row.isStrategy ? `${row.legs.length} legs` : formatQty(row.quantity)}</td>
          <td className="vd-num">{formatPrice(row.avgEntryPrice)}</td>
          <td className="vd-num">{formatPrice(row.currentPrice)}</td>
          <td className={`vd-num ${sentimentClass(row.unrealizedPl)}`}>
            {fmtCurrency(row.unrealizedPl, { signed: true })}
          </td>
          <td className="vd-num">—</td>
          <td className={`vd-num ${sentimentClass(row.unrealizedPlpc)}`}>
            {formatRoi(row.unrealizedPlpc)}
          </td>
          <td><span className="vd-position-status vd-position-status-open">Open</span></td>
        </tr>
      ))}
    </>
  );
}

export default function PositionReconciliationSummary({
  reconciliation = null,
  monthKey = null,
  monthLabelText = '',
  className = '',
  broker = null,
  supportsPerSymbolPositions = true,
}) {
  if (!reconciliation) return null;

  const {
    realized,
    unrealized,
    monthRealized,
    netPnl,
    equity,
    other,
  } = reconciliation;

  const showMonth = monthKey && monthRealized != null;
  const unrealizedLabel = broker === 'NINJA'
    ? 'Unrealized (open P/L)'
    : 'Unrealized (open positions)';

  return (
    <div className={`vd-reconciliation${className ? ` ${className}` : ''}`}>
      <h4 className="vd-reconciliation-title">Reconciliation</h4>
      <table className="vd-reconciliation-table">
        <tbody>
          {showMonth ? (
            <tr>
              <th scope="row">Realized ({monthLabelText || 'selected month'})</th>
              <td className={`vd-num ${sentimentClass(monthRealized)}`}>
                {fmtCurrency(monthRealized, { signed: true })}
              </td>
            </tr>
          ) : null}
          <tr>
            <th scope="row">Realized (all closed trades)</th>
            <td className={`vd-num ${sentimentClass(realized)}`}>
              {fmtCurrency(realized, { signed: true })}
            </td>
          </tr>
          <tr>
            <th scope="row">{unrealizedLabel}</th>
            <td className={`vd-num ${sentimentClass(unrealized)}`}>
              {fmtCurrency(unrealized, { signed: true })}
            </td>
          </tr>
          {other != null ? (
            <tr>
              <th scope="row">Other (dividends, fees, timing)</th>
              <td className={`vd-num ${sentimentClass(other)}`}>
                {fmtCurrency(other, { signed: true })}
              </td>
            </tr>
          ) : null}
          <tr className="vd-reconciliation-total-row">
            <th scope="row">Net account P&amp;L</th>
            <td className={`vd-num ${sentimentClass(netPnl)}`}>
              <strong>{fmtCurrency(netPnl, { signed: true })}</strong>
            </td>
          </tr>
          <tr>
            <th scope="row">Current equity</th>
            <td className="vd-num">{fmtCurrency(equity)}</td>
          </tr>
        </tbody>
      </table>
      <p className="vd-reconciliation-note">
        {reconciliationNote(broker, supportsPerSymbolPositions)}
      </p>
    </div>
  );
}
